import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import {
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import DeleteOutlineOutlinedIcon from "@material-ui/icons/DeleteOutlineOutlined";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import PowerSettingsNewIcon from "@material-ui/icons/PowerSettingsNew";
import AddIcon from "@material-ui/icons/Add";
import AccountTreeIcon from "@material-ui/icons/AccountTree";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import useConfirm from "../../hooks/useConfirm";
import MainContainer from "../../components/MainContainer";
import PageHeader from "../../components/PageHeader";
import DataTable from "../../components/DataTable";
import StatusChip from "../../components/StatusChip";

const useStyles = makeStyles(theme => ({
  desc: { color: theme.palette.text.secondary, display: "block", maxWidth: 320 },
  date: { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  activeToggle: { color: theme.palette.success.main },
}));

// Os status vinham crus na tela ("draft", "archived") e com fundo pastel
// fixo, ilegivel no tema escuro.
const STATUS_META = {
  active: { label: "Ativo", tone: "success" },
  draft: { label: "Rascunho", tone: "warning" },
  inactive: { label: "Inativo", tone: "neutral" },
  archived: { label: "Arquivado", tone: "danger" }
};

const AiFlows = () => {
  const classes = useStyles();
  const [confirm, confirmDialog] = useConfirm();
  const history = useHistory();

  const [flows, setFlows] = useState([]);
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: "",
    description: "",
    queueId: "",
    defaultModel: "gpt-4o-mini"
  });
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [flowsRes, queuesRes] = await Promise.all([
        api.get("/ai-flows"),
        api.get("/queue")
      ]);
      setFlows(flowsRes.data || []);
      setQueues(queuesRes.data || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = async () => {
    if (!createForm.name.trim()) {
      toast.error("Informe o nome do fluxo.");
      return;
    }
    try {
      const { data } = await api.post("/ai-flows", {
        name: createForm.name.trim(),
        description: createForm.description || undefined,
        queueId: createForm.queueId || undefined,
        defaultModel: createForm.defaultModel || undefined
      });
      toast.success("Fluxo criado.");
      setCreateOpen(false);
      setCreateForm({
        name: "",
        description: "",
        queueId: "",
        defaultModel: "gpt-4o-mini"
      });
      history.push(`/ai-flows/${data.id}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleToggle = async flow => {
    setBusyId(flow.id);
    try {
      const { data } = await api.post(`/ai-flows/${flow.id}/toggle`);
      setFlows(prev => prev.map(f => (f.id === flow.id ? { ...f, ...data } : f)));
      toast.success(
        data.status === "active" ? "Fluxo ativado." : "Fluxo desativado."
      );
    } catch (err) {
      toastError(err);
    } finally {
      setBusyId(null);
    }
  };

  const handleDuplicate = async flow => {
    setBusyId(flow.id);
    try {
      const { data } = await api.post(`/ai-flows/${flow.id}/duplicate`);
      toast.success("Fluxo duplicado.");
      history.push(`/ai-flows/${data.id}`);
    } catch (err) {
      toastError(err);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async flow => {
    if (!(await confirm({ title: `Excluir o fluxo "${flow.name}"?`, message: "Nós, conexões e o histórico de execuções são removidos junto." }))) return;
    setBusyId(flow.id);
    try {
      await api.delete(`/ai-flows/${flow.id}`);
      setFlows(prev => prev.filter(f => f.id !== flow.id));
      toast.success("Fluxo excluido.");
    } catch (err) {
      toastError(err);
    } finally {
      setBusyId(null);
    }
  };

  const columns = [
    {
      key: "name",
      label: "Nome",
      render: flow => (
        <div>
          <Typography variant="subtitle2">{flow.name}</Typography>
          {flow.description && (
            <Typography variant="caption" className={classes.desc} noWrap>
              {flow.description}
            </Typography>
          )}
        </div>
      ),
    },
    {
      key: "status",
      label: "Status",
      render: flow => {
        const meta = STATUS_META[flow.status] || { label: flow.status, tone: "neutral" };
        return <StatusChip dot tone={meta.tone} label={meta.label} />;
      },
    },
    { key: "queue", label: "Fila", render: flow => flow.queue?.name || "—" },
    { key: "whatsapp", label: "WhatsApp", render: flow => flow.whatsapp?.name || "—" },
    { key: "providerType", label: "Provedor", render: flow => flow.providerType },
    {
      key: "updatedAt",
      label: "Atualizado",
      render: flow => (
        <span className={classes.date}>
          {flow.updatedAt ? new Date(flow.updatedAt).toLocaleString("pt-BR") : "—"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Ações",
      type: "actions",
      render: flow => (
        <>
          <Tooltip title="Editar">
            <IconButton size="small" onClick={() => history.push(`/ai-flows/${flow.id}`)}>
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={flow.status === "active" ? "Desativar" : "Ativar"}>
            <IconButton
              size="small"
              disabled={busyId === flow.id}
              onClick={() => handleToggle(flow)}
              className={flow.status === "active" ? classes.activeToggle : undefined}
            >
              <PowerSettingsNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Duplicar">
            <IconButton size="small" disabled={busyId === flow.id} onClick={() => handleDuplicate(flow)}>
              <FileCopyOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton size="small" disabled={busyId === flow.id} onClick={() => handleDelete(flow)}>
              <DeleteOutlineOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  return (
    <MainContainer>
      {confirmDialog}
      <PageHeader
        title="Fluxos de Atendimento com IA"
        count={flows.length}
        subtitle="Conversas conduzidas por IA em etapas: cada nó decide o que perguntar, consultar ou encaminhar."
        actions={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            Novo fluxo
          </Button>
        }
      />

      <DataTable
        columns={columns}
        rows={flows}
        loading={loading}
        emptyIcon={<AccountTreeIcon />}
        emptyTitle="Nenhum fluxo cadastrado"
        emptyDescription="Um fluxo atende sozinho as primeiras mensagens — qualifica, responde dúvidas comuns e só passa para um humano quando precisa."
        emptyAction={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            Novo fluxo
          </Button>
        }
      />

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Novo fluxo de atendimento</DialogTitle>
        <DialogContent>
          <TextField
            label="Nome *"
            value={createForm.name}
            onChange={e => {
              const value = e.target.value;
              setCreateForm(prev => ({ ...prev, name: value }));
            }}
            fullWidth
            variant="outlined"
            size="small"
            style={{ marginTop: 8 }}
          />
          <TextField
            label="Descricao"
            value={createForm.description}
            onChange={e => {
              const value = e.target.value;
              setCreateForm(prev => ({ ...prev, description: value }));
            }}
            fullWidth
            variant="outlined"
            size="small"
            multiline
            minRows={2}
            style={{ marginTop: 12 }}
          />
          <TextField
            select
            label="Fila vinculada"
            value={createForm.queueId}
            onChange={e => {
              const value = e.target.value;
              setCreateForm(prev => ({ ...prev, queueId: value }));
            }}
            fullWidth
            variant="outlined"
            size="small"
            style={{ marginTop: 12 }}
            helperText="O fluxo so executa para tickets desta fila. Deixe vazio para nao filtrar."
          >
            <MenuItem value="">Sem filtro de fila</MenuItem>
            {queues.map(q => (
              <MenuItem key={q.id} value={q.id}>
                {q.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Modelo padrao OpenAI"
            value={createForm.defaultModel}
            onChange={e => {
              const value = e.target.value;
              setCreateForm(prev => ({ ...prev, defaultModel: value }));
            }}
            fullWidth
            variant="outlined"
            size="small"
            style={{ marginTop: 12 }}
            helperText="ex.: gpt-4o-mini, gpt-4o"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancelar</Button>
          <Button onClick={handleCreate} variant="contained" color="primary">
            Criar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default AiFlows;
